import { IconShipAsReady, IconShipTogether } from '../../components/icons'
import { Card } from '../../components/ui/Card'
import type { components } from '../../lib/api/schema.gen'
import { CartQuoteOptionSelector } from './CartQuoteOptionSelector'
import { ShipmentBreakdown } from './ShipmentBreakdown'
import type { RegionTierId } from './custom-region-tiers'
import { deriveShipmentGroups } from './ship-as-ready-breakdown'

type ShipPlan = components['schemas']['ShipPlan']
type ShipMode = components['schemas']['ShipMode']
type DeliveryOption = components['schemas']['DeliveryOption']
type LineItem = components['schemas']['LineItem']
type CartLinePresentation = components['schemas']['CartLinePresentation']
type CartDeliveryOptionTotalEntry = components['schemas']['CartDeliveryOptionTotalEntry']

export interface ShipPlanCardProps {
  readonly shipPlan: ShipPlan | undefined
  readonly deliveryOptions: readonly DeliveryOption[]
  /** UID-622 (ADR-0181 D1/D2) — the server-derived order total per delivery
   * option, passed straight through to `CartQuoteOptionSelector` as each
   * lead-time row's headline figure. Optional for callers/tests that have no
   * cart view to hand: the selector then shows its "Total not available"
   * caption rather than falling back to a shipping charge shown as a total,
   * which is the defect UID-622 closes. */
  readonly deliveryOptionTotals?: readonly CartDeliveryOptionTotalEntry[] | undefined
  readonly busy?: boolean
  readonly onSelectMode: (mode: ShipMode) => void
  readonly onSelectDeliveryOption: (deliveryOptionId: string) => void
  /** UID-96 (C1) — the cart's lines + presentations, to render the "Ship as
   * ready" multi-shipment breakdown. Optional so existing callers that don't
   * pass them (and tests) keep the plain explanatory caption. Also drives
   * the "Ship as ready" tab's disabled/"— single delivery" state (see the
   * component doc) — omitting them keeps that tab enabled, same as before
   * this fix, rather than guessing. */
  readonly lines?: readonly LineItem[]
  readonly presentationsBySourceableId?: ReadonlyMap<string, CartLinePresentation>
  /** UID-96 (C2) — the custom bucket's selected lead-time tier + setter,
   * threaded through to the breakdown's `CustomLeadTimeControl`. `null`
   * (code-review M1) when the persisted tier id isn't recognized — see
   * `CustomLeadTimeControl`'s doc; this default prop value of `'us'` only
   * applies when the prop is omitted entirely (existing callers/tests with
   * no tier data at all), never as a stand-in for an unresolved id. Renamed
   * from `customRegionTierId`/`onSelectCustomRegionTier`/`customRegionTierBusy`
   * (code-review Fix 2) for consistency with the rest of this ticket's
   * "lead-time" naming (`CartPage`'s state/handler, `CustomLeadTimeControl`,
   * the CSS, the endpoint) — this prop always carries a lead-time-tier id,
   * never a "region" per se. */
  readonly customLeadTimeTierId?: RegionTierId | null
  readonly onSelectCustomLeadTimeTier?: (tierId: RegionTierId) => void
  /** UID-271 — disables the lead-time control while its round trip
   * (`PUT /cart/lines/{sourceableId}/lead-time-tier`) is pending. */
  readonly customLeadTimeTierBusy?: boolean
}

/**
 * The Cart screen's lead-time / ship-plan selector (Cart redesign, top of
 * the line-item column): a "Ship everything together" / "Ship as ready"
 * choice, and — in ship-together mode — a region/quote-option choice
 * (UID-440 cart follow-up: `CartQuoteOptionSelector`, the RFQ-style
 * 4-variant/2-column North America/Overseas picker — REPLACING the earlier
 * 3-card EXPEDITED/STANDARD/OVERSEAS `DeliveryOptionCard` grid). The
 * mode choice is a real radio GROUP (`<fieldset>`/`<legend>` + native
 * radios), per this build's brief ("the ship-plan tabs ... as proper radio
 * groups") rather than the mock's plain `<div onClick>` tab styling — a
 * mutually-exclusive choice from a small fixed set is exactly what a radio
 * group's semantics are for, and it comes with keyboard/AT support for
 * free. `CartQuoteOptionSelector` is its own nested radiogroup (same
 * pattern); UID-456 — it now initializes its default-checked variant FROM
 * the cart's persisted `selectedDeliveryOptionId` (reverse-mapped via
 * `variantForDeliveryOption`, see that component's doc comment), so this
 * card resolves and passes that id down rather than leaving the selector to
 * always default to the first canonical variant regardless of what the
 * cart actually has selected.
 *
 * `shipPlan` is `undefined` until the buyer has chosen at all (a fresh
 * cart) — this renders the "ship together" tab active in that case,
 * prompting a real choice rather than silently assuming one server-side.
 */
export function ShipPlanCard({
  shipPlan,
  deliveryOptions,
  deliveryOptionTotals,
  busy = false,
  onSelectMode,
  onSelectDeliveryOption,
  lines,
  presentationsBySourceableId,
  customLeadTimeTierId = 'us',
  onSelectCustomLeadTimeTier,
  customLeadTimeTierBusy = false,
}: ShipPlanCardProps) {
  const mode: ShipMode = shipPlan?.mode ?? 'ship_together'

  /**
   * Design-fidelity fix (per the user's rendered design screenshot):
   * "Ship as ready" is disabled when the cart would resolve to only ONE
   * shipment anyway — nothing to stage across multiple shipments, so the
   * choice is moot. Condition used: `deriveShipmentGroups(...).length <= 1`
   * — the SAME readiness-bucket grouping `ShipmentBreakdown` already
   * renders (UID-96 C1), not a cruder proxy like "only one line" (two
   * lines in the SAME bucket, e.g. two catalog parts, still resolve to one
   * shipment group and should also disable the tab; the group count is the
   * actual signal, not the line count). Only computed when `lines` +
   * `presentationsBySourceableId` are actually supplied — callers that
   * don't pass them (and existing tests) get the pre-existing behavior
   * (never disabled), since there's no data to determine "single shipment"
   * from.
   */
  const shipAsReadyIsSingleShipment =
    lines !== undefined &&
    presentationsBySourceableId !== undefined &&
    deriveShipmentGroups(lines, presentationsBySourceableId).length <= 1
  const shipAsReadyDisabled = busy || shipAsReadyIsSingleShipment

  // UID-456 — the cart's REAL persisted delivery-option selection, `null` in ship-as-ready
  // mode or before any choice has been made (mirrors the pre-UID-440 `DeliveryOptionCard`
  // grid's own `selectedDeliveryOptionId` derivation, restored here for
  // `CartQuoteOptionSelector` to reverse-map into its default-checked variant).
  const selectedDeliveryOptionId =
    shipPlan?.mode === 'ship_together' ? shipPlan.selectedDeliveryOptionId : null

  return (
    <Card className="ship-plan-card" aria-labelledby="ship-plan-heading">
      <h2 id="ship-plan-heading" className="sr-only">
        Ship plan
      </h2>
      <fieldset className="ship-plan-mode-fieldset">
        <legend className="sr-only">How should this order ship?</legend>
        <div className="ship-plan-mode-tabs">
          <label
            className={[
              'ship-plan-mode-tab',
              mode === 'ship_together' && 'ship-plan-mode-tab-active',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <input
              type="radio"
              name="ship-plan-mode"
              value="ship_together"
              checked={mode === 'ship_together'}
              disabled={busy}
              onChange={() => onSelectMode('ship_together')}
              className="ship-plan-mode-tab-input"
            />
            <IconShipTogether />
            Ship everything together
          </label>
          <label
            className={[
              'ship-plan-mode-tab',
              mode === 'ship_as_ready' && 'ship-plan-mode-tab-active',
              shipAsReadyIsSingleShipment && 'ship-plan-mode-tab-disabled',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <input
              type="radio"
              name="ship-plan-mode"
              value="ship_as_ready"
              checked={mode === 'ship_as_ready'}
              disabled={shipAsReadyDisabled}
              aria-disabled={shipAsReadyDisabled}
              onChange={() => onSelectMode('ship_as_ready')}
              className="ship-plan-mode-tab-input"
            />
            <IconShipAsReady />
            Ship as ready
            {shipAsReadyIsSingleShipment && <span> — single delivery</span>}
          </label>
        </div>
      </fieldset>

      <div className="ship-plan-body">
        {mode === 'ship_together' ? (
          // UID-440 (cart follow-up) — REPLACES the old 3-card
          // `.delivery-option-grid` (expedited/standard/overseas,
          // `DeliveryOptionCard`) with the RFQ-style 4-variant, 2-column
          // region selector. `CartQuoteOptionSelector` supplies its OWN
          // heading/chip/subcopy framing (cart-appropriate copy, never the
          // RFQ selector's post-quote framing) — it takes over the job the
          // plain `ship-plan-intro` paragraph used to do for this mode, so
          // that paragraph is removed here (still rendered verbatim, and
          // UNCHANGED, in `ship_as_ready` mode below). Falls back to an
          // empty lines array when the caller doesn't supply `lines` (kept
          // optional for existing callers/tests) — the preview then shows
          // "$0.00 /ea" for every variant rather than crashing, same
          // graceful-degradation posture as `applyVariantRatio` itself.
          <CartQuoteOptionSelector
            lines={lines ?? []}
            currency={deliveryOptions[0]?.currency ?? lines?.[0]?.price?.currency ?? 'USD'}
            disabled={busy}
            deliveryOptions={deliveryOptions}
            deliveryOptionTotals={deliveryOptionTotals ?? []}
            selectedDeliveryOptionId={selectedDeliveryOptionId}
            onSelectDeliveryOption={onSelectDeliveryOption}
          />
        ) : (
          <>
            {/* Design-fidelity fix (UID-271 Part B): exact prototype subtitle
                copy for the split-card view (`shipCard.subtitle` when
                `_plan==='split'`, Fictiv Platform.dc.html line ~9032). */}
            <p className="caption-text ship-plan-intro">
              Each shipment leaves as soon as its parts are ready. Adjust the lead time on the final
              shipment below.
            </p>
            {lines && presentationsBySourceableId && onSelectCustomLeadTimeTier && (
              <ShipmentBreakdown
                lines={lines}
                presentationsBySourceableId={presentationsBySourceableId}
                customLeadTimeTierId={customLeadTimeTierId}
                onSelectCustomLeadTimeTier={onSelectCustomLeadTimeTier}
                customLeadTimeTierBusy={customLeadTimeTierBusy}
              />
            )}
          </>
        )}
      </div>
    </Card>
  )
}
